import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
    Button,
    Chip,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownTrigger,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ScrollShadow,
    useDisclosure,
    type SharedSelection,
} from "@heroui/react";
import {
    IconCheck,
    IconChevronDown,
    IconDeviceFloppy,
    IconPlus,
    IconX,
} from "@tabler/icons-react";
import type { CollectionElement } from "@react-types/shared";
import { useResumeBuilder } from "../context";

export const ItemList = ({
    items,
    selectPlaceholder,
    addCustomText,
    addCustomShortText,
    addCustomInputPlaceholder = "Benutzerdefiniert",
    initialValues,
    onChange,
}: {
    items: string[];
    selectPlaceholder: string;
    addCustomText: string;
    addCustomShortText?: string;
    addCustomInputPlaceholder?: string;
    initialValues?: string[];
    onChange?: (values: SharedSelection) => void;
}) => {
    const { design } = useResumeBuilder();
    const [inputValue, setInputValue] = useState("");
    const initialValuesSet = new Set(Array.from(initialValues));
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const [menuWidth, setMenuWidth] = useState<number>();
    const customSkillModalDisclosure = useDisclosure();

    const handleAddInput = () => {
        if (!inputValue) {
            return;
        }

        onChange?.(new Set([...initialValues, inputValue]));
        setInputValue("");
        customSkillModalDisclosure.onClose();
    };

    const handleRemoveKey = (key: string) => {
        initialValuesSet.delete(key);
        onChange?.(initialValuesSet);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddInput();
        }
    };

    const handleToggleTemplate = (value?: string) => {
        if (!value) {
            return;
        }

        if (initialValuesSet.has(value)) {
            handleRemoveKey(value);
            return;
        }

        onChange?.(new Set([...initialValues, value]));
        setInputValue("");
    };

    useEffect(() => {
        if (triggerRef.current) {
            const width = triggerRef.current.getBoundingClientRect().width;
            setMenuWidth(width);
        }
    }, []);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex gap-4">
                <Dropdown closeOnSelect={false}>
                    <DropdownTrigger className="justify-between">
                        <Button
                            size="md"
                            ref={triggerRef}
                            variant="flat"
                            disableRipple={true}
                            as="div"
                            className="flex-1"
                            endContent={
                                <IconChevronDown stroke={1.5} size={20} />
                            }
                        >
                            {selectPlaceholder}
                        </Button>
                    </DropdownTrigger>
                    <ScrollShadow
                        as={DropdownMenu}
                        aria-label="Profile Actions"
                        variant="flat"
                        style={{ width: menuWidth }}
                        className="no-scrollbar max-h-[400px] overflow-y-auto"
                        classNames={{
                            base: "max-h-[600px] overflow-scroll w-full",
                        }}
                    >
                        {
                            items.map((item) => (
                                <DropdownItem
                                    key={item}
                                    onPress={() => handleToggleTemplate(item)}
                                    endContent={
                                        initialValuesSet.has(item) ? (
                                            <IconCheck stroke={1.5} size={18} />
                                        ) : null
                                    }
                                >
                                    <div>{item}</div>
                                </DropdownItem>
                            )) as unknown as CollectionElement<object>
                        }
                    </ScrollShadow>
                </Dropdown>
                <Button
                    variant="flat"
                    size="md"
                    onPress={customSkillModalDisclosure.onOpen}
                >
                    <div className="hidden sm:block">{addCustomText}</div>
                    <div className="block sm:hidden">
                        {addCustomShortText || addCustomText}
                    </div>
                    <IconPlus stroke={1.5} size={20} />
                </Button>
            </div>
            {initialValues.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {initialValues.map((key) => (
                        <Chip
                            key={key}
                            size="lg"
                            variant="bordered"
                            className="h-auto min-h-8 whitespace-normal py-1 text-sm"
                            endContent={
                                <button
                                    type="button"
                                    onClick={() => handleRemoveKey(key)}
                                    className="cursor-pointer"
                                >
                                    <IconX stroke={1.5} size={18} />
                                </button>
                            }
                        >
                            {key}
                        </Chip>
                    ))}
                </div>
            )}
            <Modal
                isOpen={customSkillModalDisclosure.isOpen}
                onOpenChange={customSkillModalDisclosure.onOpenChange}
                backdrop="blur"
                size="3xl"
                // fixes: https://github.com/heroui-inc/heroui/issues/3937#issuecomment-2620261488
                placement="center"
                shouldBlockScroll={false}
                disableAnimation={true}
                classNames={{
                    wrapper: "items-start h-auto foobar z-[999999]",
                    backdrop: "z-[999999]",
                    base: "my-auto",
                }}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader />
                            <ModalBody>
                                <Input
                                    value={inputValue}
                                    label={addCustomInputPlaceholder}
                                    size="lg"
                                    variant="flat"
                                    onValueChange={setInputValue}
                                    onKeyDown={handleKeyDown}
                                />
                            </ModalBody>
                            <ModalFooter>
                                <Button variant="light" onPress={onClose}>
                                    Abbrechen
                                </Button>
                                <Button
                                    onPress={handleAddInput}
                                    isDisabled={!inputValue}
                                    startContent={
                                        <IconDeviceFloppy
                                            stroke={1.5}
                                            size={20}
                                        />
                                    }
                                    style={{
                                        backgroundColor:
                                            design.resumePrimaryColor,
                                        color: design.resumePrimaryContrastColor,
                                    }}
                                >
                                    Hinzufügen
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
};
